// PWA и Web Push на стороне браузера
import { api } from '../stores/auth'

const lsGet = (k) => { try { return localStorage.getItem(k) } catch { return null } }
const lsSet = (k, v) => { try { localStorage.setItem(k, v) } catch { /* приватный режим */ } }

export const isStandalone = () =>
  (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || window.navigator.standalone === true
export const isIOS = () => /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
const isInAppBrowser = () => /Telegram|FBAN|FBAV|Instagram|VKAndroidApp|VK\/|; wv\)/.test(navigator.userAgent)

// ---------- установка ----------
let deferredPrompt = null
const listeners = new Set()
const notify = () => listeners.forEach(fn => fn())
export function onPwaChange(fn) { listeners.add(fn); return () => listeners.delete(fn) }

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); deferredPrompt = e; notify() })
  window.addEventListener('appinstalled', () => { deferredPrompt = null; notify() })
}
export const canPromptInstall = () => !!deferredPrompt
export async function promptInstall() {
  if (!deferredPrompt) return false
  deferredPrompt.prompt()
  const r = await deferredPrompt.userChoice
  deferredPrompt = null
  notify()
  return r.outcome === 'accepted'
}

export const INSTALL_DISMISS_KEY = 'pwa_install_dismissed_at'
export const installDismissed = () => Date.now() - Number(lsGet(INSTALL_DISMISS_KEY) || 0) < 14 * 864e5
export const dismissInstall = () => lsSet(INSTALL_DISMISS_KEY, String(Date.now()))

// ---------- service worker ----------
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator) || !window.isSecureContext) return null
  try { return await navigator.serviceWorker.register('/sw.js', { scope: '/' }) } catch { return null }
}

// ---------- push ----------
/** Почему push недоступен: null (всё можно), либо код причины */
export function pushBlocker() {
  if (!window.isSecureContext) return 'insecure'
  if (isIOS() && !isStandalone()) return 'ios-install'
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return isInAppBrowser() ? 'inapp' : 'unsupported'
  if (Notification.permission === 'denied') return 'denied'
  return null
}
export const BLOCKER_TEXT = {
  insecure: 'Уведомления работают только на защищённом соединении (https).',
  'ios-install': 'На iPhone и iPad уведомления приходят только в установленном приложении: нажмите «Поделиться» в Safari и выберите «На экран Домой», затем откройте QClient с экрана.',
  inapp: 'Это встроенный браузер мессенджера: уведомления в нём недоступны. Откройте QClient в Chrome, Safari или Яндекс Браузере.',
  unsupported: 'Этот браузер не поддерживает уведомления. Попробуйте Chrome, Safari или Яндекс Браузер.',
  denied: 'Уведомления запрещены в настройках браузера для этого сайта. Разрешите их в настройках сайта и вернитесь сюда.'
}

function urlBase64ToUint8Array(b64) {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4)
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'))
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)))
}

export async function getPushConfig() {
  const { data } = await api.get('/push/config')
  return data
}

export async function currentSubscription() {
  const reg = await navigator.serviceWorker.getRegistration('/')
  return reg ? reg.pushManager.getSubscription() : null
}

export async function endpointHash(sub) {
  if (!sub) return null
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(sub.endpoint))
  return [...new Uint8Array(buf)].map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16)
}

/** Запрос разрешения и подписка. Вызывать только из клика (требование iOS и Chrome) */
export async function enablePush() {
  const cfg = await getPushConfig()
  if (!cfg.enabled || !cfg.publicKey) throw new Error('Уведомления пока не настроены на сервере')
  const perm = await Notification.requestPermission()
  if (perm !== 'granted') throw new Error('Разрешение на уведомления не выдано')
  const reg = (await navigator.serviceWorker.getRegistration('/')) || (await registerServiceWorker())
  if (!reg) throw new Error('Не удалось запустить службу уведомлений')
  await navigator.serviceWorker.ready
  let sub = await reg.pushManager.getSubscription()
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(cfg.publicKey) })
  await api.post('/push/subscribe', { subscription: sub.toJSON() })
  return sub
}

export async function disablePush() {
  const sub = await currentSubscription()
  if (!sub) return
  try { await api.post('/push/unsubscribe', { endpoint: sub.endpoint }) } catch { /* сервер недоступен: ниже всё равно отпишемся локально */ }
  await sub.unsubscribe()
}

/** При каждом открытии: если разрешение уже выдано, тихо обновляем подписку на сервере (она могла смениться) */
export async function syncPushSilently() {
  try {
    if (pushBlocker() || Notification.permission !== 'granted') return
    const sub = await currentSubscription()
    if (sub) await api.post('/push/subscribe', { subscription: sub.toJSON() })
  } catch { /* не критично */ }
}

export async function clearBadge() {
  try { if (navigator.clearAppBadge) await navigator.clearAppBadge() } catch { /* не поддерживается */ }
}
